import * as Context from "effect/Context";
import * as Data from "effect/Data";
import * as Effect from "effect/Effect";
import * as Layer from "effect/Layer";
import * as Option from "effect/Option";
export const Provider = (type) => Context.Service()(type);
const withDefaultList = (service) => service.list ? service : { ...service, list: () => Effect.succeed([]) };
export const effect = (cls, eff) => Layer.effect(
// @ts-expect-error
Provider(cls.Type), Effect.map(eff, (service) => ({
    aliases: "Aliases" in cls ? cls.Aliases : undefined,
    ...withDefaultList(service),
})));
export const succeed = (cls, service) => 
// @ts-expect-error
Layer.succeed(Provider(cls.Type), {
    aliases: "Aliases" in cls ? cls.Aliases : undefined,
    ...withDefaultList(service),
});
export const ProviderCollection = () => (id) => Context.Service()(id);
export const collection = (resources) => Effect.gen(function* () {
    const context = yield* Effect.context();
    const providers = Object.fromEntries(yield* Effect.all(resources.map((resource) => "Provider" in resource
        ? resource.Provider.pipe(Effect.map((provider) => [resource.Type, provider]))
        : Effect.succeed([
            resource.key,
            context.mapUnsafe.get(resource.key),
        ])), { concurrency: "unbounded" }));
    return {
        kind: "ProviderCollection",
        get: (service) => providers[service],
        providers,
    };
});
const isProviderCollectionService = (value) => {
    return (typeof value === "object" &&
        value !== null &&
        "kind" in value &&
        value.kind === "ProviderCollection");
};
/**
 * Structural check for a {@link ProviderService} living in the Effect
 * context. Providers are keyed by their canonical resource type; when
 * searching for a legacy alias, the tag key won't match, so lookup has to
 * recognize provider services by shape.
 */
const isProviderService = (value) => typeof value === "object" &&
    value !== null &&
    "reconcile" in value &&
    typeof value.reconcile === "function" &&
    "delete" in value &&
    typeof value.delete === "function";
/**
 * Resolve the concrete service for `mode` from a provider found in context.
 *
 * - `mode === undefined` → the service as registered (for dual
 *   registrations that is the delegating service for the run's default
 *   mode).
 * - `mode` given and the provider carries {@link ProviderService.modes} →
 *   the (lazily built, memoized) variant for that mode.
 * - `mode` given but the provider is mode-agnostic → the service itself:
 *   a single implementation satisfies every mode (hybrid), which is what
 *   lets live-only resources (e.g. R2 buckets) participate in dev runs.
 */
export const providerForMode = (service, mode) => mode !== undefined && service.modes !== undefined
    ? service.modes[mode]
    : Effect.succeed(service);
export const findProviderByType = ((type, mode) => tryFindProviderByType(type, mode).pipe(Effect.flatMap(Option.match({
    onNone: () => Effect.die(`Provider not found for ${type}`),
    onSome: (provider) => Effect.succeed(provider),
}))));
/**
 * Typed provider lookup by resource class (or {@link Platform}) value. Infers
 * `R` from the class so `provider.list()` / `provider.read(...)` return the
 * resource's `Attributes` shape — prefer this over {@link findProviderByType},
 * which only takes the type string.
 */
export const findProvider = (resource, mode) => findProviderByType((resource.Type ?? resource.key), mode);
/**
 * A persisted state row references a resource type with no registered
 * provider — the type was removed from the program (or renamed without an
 * alias) while its state row still exists ("zombie" row).
 *
 * This is FATAL at plan time: the program and state fundamentally disagree,
 * and without the provider the row's physical resource cannot be deleted
 * anyway, so proceeding would only strand it silently. The plan dies with
 * this error; nothing is deployed or destroyed until the provider is
 * re-registered (or aliased), or the state row is cleared manually.
 */
export class MissingProviderError extends Data.TaggedError("MissingProviderError") {
}
/** Build the fatal plan-time error for a zombie state row. */
export const missingProviderError = (resourceType, fqn) => new MissingProviderError({
    message: `No provider is registered for resource type '${resourceType}' ` +
        `(state row '${fqn}'). The type was removed from the program or ` +
        "renamed without an alias. Re-register the provider (or add the " +
        "old name to the resource's `aliases`) so this row can be " +
        "destroyed, or clear the state row manually if the physical " +
        "resource is already gone.",
    resourceType,
    fqn,
});
export const tryFindProviderByType = Effect.fn(function* (resourceType, mode) {
    // When a mode is requested, resolve the found service to that mode's
    // variant (building it lazily if needed) before returning.
    const found = yield* Effect.gen(function* () {
        const Tag = Provider(resourceType);
        const direct = yield* Effect.serviceOption(Tag);
        if (Option.isSome(direct)) {
            return direct;
        }
        const context = yield* Effect.context();
        for (const value of context.mapUnsafe.values()) {
            if (isProviderCollectionService(value)) {
                const provider = value.get(resourceType);
                if (provider) {
                    return Option.some(provider);
                }
            }
        }
        // State persisted before a type rename carries the legacy name, so no
        // provider is keyed under it. Fall back to a provider that declares the
        // name in its `aliases` — scanning both bare Provider layers and the
        // members of every ProviderCollection.
        for (const value of context.mapUnsafe.values()) {
            if (isProviderCollectionService(value)) {
                for (const provider of Object.values(value.providers)) {
                    if (provider.aliases?.includes(resourceType)) {
                        return Option.some(provider);
                    }
                }
            }
            else if (isProviderService(value) &&
                value.aliases?.includes(resourceType)) {
                return Option.some(value);
            }
        }
        return Option.none();
    });
    if (Option.isNone(found)) {
        return found;
    }
    return Option.some(yield* providerForMode(found.value, mode));
});
//# sourceMappingURL=Provider.js.map